package com.iregained.site.components.sections.about.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.about.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Profile Float
@Composable
private fun Modifier.profileFloat(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 30.px).transition(
        Transition.of("opacity", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun AboutLeadershipSection(data: AboutLeadershipSectionData = defaultAboutLeadershipData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "about-leadership-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.sectionTitle.isNotEmpty()) {
        Column(
            modifier = AboutLeadershipContainerStyle.toModifier().id(targetId).fillMaxWidth()
                .backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    SectionHeader(
                        text = data.sectionTitle,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s)),
                        alignment = Alignment.CenterHorizontally
                    )

                    if (data.sectionSubtitle.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 60.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s, delay = 150.ms)).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.sectionSubtitle; onDispose {}
                                    }
                                })
                    }

                    if (data.executives.isNotEmpty()) {
                        SimpleGrid(
                            numColumns(base = 1, sm = 2, lg = 3), modifier = AboutLeadershipGridStyle.toModifier()
                        ) {
                            data.executives.forEachIndexed { index, member ->
                                key(member.name) {
                                    Box(modifier = Modifier.profileFloat(isVisible, 300 + (index * 150))) {
                                        LeadershipCard(member, palette)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LeadershipCard(member: LeadershipMemberData, palette: com.iregained.site.theme.SitePalette) {
    Box(modifier = LeadershipCardStyle.toModifier()) {
        Column(modifier = Modifier.fillMaxSize(), horizontalAlignment = Alignment.Start) {
            val hasLinkedin = member.linkedinUrl.isNotBlank()
            Box(modifier = Modifier.fillMaxWidth().margin(bottom = 24.px), contentAlignment = Alignment.Center) {
                val imageContent = @Composable {
                    if (member.imagePath.isNotBlank()) {
                        Box(
                            modifier = LeadershipImageContainerStyle.toModifier(), contentAlignment = Alignment.Center
                        ) {
                            Image(
                                src = member.imagePath,
                                alt = "${member.name} - ${member.title}",
                                modifier = LeadershipImageStyle.toModifier()
                                    .thenIf(hasLinkedin) { Modifier.cursor(Cursor.Pointer) })
                            if (hasLinkedin) {
                                Box(modifier = LeadershipLinkedinIconStyle.toModifier()) {
                                    Image(
                                        src = Res.Icons.LINKEDIN,
                                        alt = "LinkedIn Profile",
                                        modifier = Modifier.size(30.px).objectFit(ObjectFit.Contain)
                                    )
                                }
                            }
                        }
                    }
                }
                if (hasLinkedin) A(
                    href = member.linkedinUrl,
                    attrs = Modifier.toAttrs { attr("target", "_blank") }) { imageContent() } else imageContent()
            }

            if (member.name.isNotBlank()) SpanText(text = member.name, modifier = LeadershipNameStyle.toModifier())
            if (member.title.isNotBlank()) SpanText(
                text = member.title, modifier = LeadershipTitleStyle.toModifier().fontWeight(FontWeight.Bold)
            )
            if (member.background.isNotBlank()) SpanText(
                text = member.background,
                modifier = Modifier.fillMaxWidth().padding(bottom = 16.px).color(Colors.Gray).fontSize(14.px)
                    .fontWeight(FontWeight.SemiBold).textAlign(TextAlign.Start)
            )
            if (member.bioSnippet.isNotBlank()) Div(
                attrs = LeadershipBioSnippetStyle.toModifier().color(palette.custom.darkText).fontSize(14.px)
                    .toAttrs { ref { htmlElement -> htmlElement.innerHTML = member.bioSnippet; onDispose {} } })
        }
    }
}

// ==========================================
// CMS DATA MODEL & MEDIA SPECS
// ==========================================
/**
 * CMS IMAGE UPLOAD SPECS FOR LEADERSHIP:
 * @field imagePath:
 * - Aspect Ratio: 1:1 (Square).
 * - Recommended Dimensions: 400x400px.
 * - Format: WebP or high-quality JPEG.
 * - Content: Professional headshots with a consistent background color (e.g., solid light gray) to ensure the grid looks like a unified team, not a collage.
 */
data class LeadershipMemberData(
    val name: String,
    val title: String,
    val background: String,
    val imagePath: String,
    val bioSnippet: String = "",
    val linkedinUrl: String = "" // Added url to default model implementation
)

data class AboutLeadershipSectionData(
    val sectionId: String? = null,
    val sectionTitle: String = "Driven by Expertise",
    val sectionSubtitle: String = "Our Leadership team unites decades of experience across mechatronic engineering, digital therapeutics, and neurorehabilitation.",
    val executives: List<LeadershipMemberData> = listOf(
        LeadershipMemberData(
            name = "Vineet Benjamin K. Johnson",
            title = "Founder & CEO",
            background = "BSC.PT., PG.DIP.PT., MSc., PhD(c)",
            imagePath = Res.Images.About.Leadership.VINEET,
            bioSnippet = "Vineet brings over 25 years of global academic, clinical, and research experience spanning Canada, the UK, and India. As a Neuroscientist with deep expertise in physical rehabilitation, his pioneering research provides the core clinical and scientific validation for IRegained’s products.",
            linkedinUrl = "https://www.linkedin.com/in/vineetjohnson/"
        ),
        LeadershipMemberData(
            name = "Joel Liederman",
            title = "BOARD CHAIR & Head, Strategic Partnerships",
            background = "BE, MBA",
            imagePath = Res.Images.About.Leadership.JOEL,
            bioSnippet = "Joel is an accomplished senior executive with more than 45 years of business leadership experience, along with domestic & international business development expertise, and venture capital experience in medical and other high technology industries.",
            linkedinUrl = "https://www.linkedin.com/in/joel-liederman-51573/"
        ),
        LeadershipMemberData(
            name = "Samir Sahoo",
            title = "COO & VP Business Development",
            background = "BE, MBA",
            imagePath = Res.Images.About.Leadership.SAMIR,
            bioSnippet = "Seasoned business executive with 15+ Years of MedTech experience. Multi-disciplinary expertise includes medical device product development, life-cycle management, technology commercialization, P/L management, start-up financing, marketing and sales in Canada, and USA.",
            linkedinUrl = "https://www.linkedin.com/in/samirsahoo/"
        ),
        LeadershipMemberData(
            name = "Ranjit Stanley",
            title = "Co-founder & CSO",
            background = "BSC.PT., RPT",
            imagePath = Res.Images.About.Leadership.RANJIT,
            bioSnippet = "Physiotherapist with 25+ years of clinical experience in the US and India. Ranjit's vast amount of hands-on clinical expertise in musculoskeletal, neurological, neuro-muscular and ortho/neuro trauma rehabilitation has been instrumental in the development of neuroplasticity enhancing clinical protocols.",
            linkedinUrl = "https://www.linkedin.com/in/ranjit-stanley/"
        ),
        LeadershipMemberData(
            name = "Laura Karik",
            title = "Chief Design Officer",
            background = "IDSA",
            imagePath = Res.Images.About.Leadership.LAURA,
            bioSnippet = "Laura has contributed to projects within the start-up, military, medical, industrial, and consumer industries. Across these diverse industries, she has bridged her accumulated knowledge of Design strategy, Usability, and fabrication technologies to provide successful product solutions at any stage of the product development life-cycle.",
            linkedinUrl = "https://www.linkedin.com/in/laura-karik-a080761/"
        ),
        LeadershipMemberData(
            name = "Michael Dolinar",
            title = "Chief Technology Officer",
            background = "B.Sc. Comp.Sc.",
            imagePath = Res.Images.About.Leadership.MIKE,
            bioSnippet = "Michael has a wealth of technology development experience in the healthcare, scientific and defense sectors. As an entrepreneur, Michael launched AdvanceWorx in 2014 to build leading-edge software apps for emerging technologies and then launched Synaptic Technologies in 2018 to build futuristic medical device for use by W.H.O. medical staff.",
            linkedinUrl = "https://www.linkedin.com/in/michaeldolinar/"
        ),
    )
)

val defaultAboutLeadershipData = AboutLeadershipSectionData()